import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  useRouteMatch,
  Route,
  Switch,
  useLocation,
  Link,
  useHistory,
} from "react-router-dom";

import qs from "query-string";
import dayjs from "dayjs";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import useDebounce from "react-use/lib/useDebounce";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Select from "react-select";
import DatePicker from "react-datepicker";

import {
  QUERY_CLIENTS_FOR_SIDEBAR_FOR_ADMIN,
  QUERY_ERROR_LOGS,
} from "../../config/graphql/query";

import ErrorLog from "./ErrorLog/index";
import Table from "../../components/Table";
import Pagination from "../../components/Pagination";

import { reactSelectCustomStyles } from "../Employees/Employee/Information";

const LogErrorTypes = ["error", "rejection"] as const;

type TLogErrorType = typeof LogErrorTypes[number];

const ErrorLogSources: ErrorLogSource[] = ["graphql", "rest", "job", "process"];

const limit = 10;

const TableRow = ({
  item,
  onClientClick,
}: {
  item: IErrorLog;
  onClientClick: (clientId: string) => void;
}) => {
  const {
    id,
    createdAt,
    errorMessage,
    level,
    errorCode,
    suppressed,
    fatal,
    clientId,
    client,
  } = item;

  const { t } = useTranslation(["errorLogs", "common"]);

  return (
    <>
      <tr>
        <td className="text-nowrap">
          {dayjs(createdAt).local().format("DD-MM-YYYY HH:mm:ss")}
        </td>
        <td
          className="text-truncate"
          title={item.operation || item.endpoint || undefined}
        >
          {item.operation || item.endpoint || "—"}
        </td>
        <td>
          <div className="d-flex align-items-center">
            <span className="text-truncate" title={errorMessage}>
              {errorMessage}
            </span>
            {!!fatal && (
              <Badge variant="danger" className="ml-2 flex-shrink-0">
                {t("errorLogs:errorLogs.fatal")}
              </Badge>
            )}
            {!!errorCode && (
              <Badge variant="secondary" className="ml-2 flex-shrink-0">
                {errorCode}
              </Badge>
            )}
            {!!suppressed && (
              <Badge
                variant="warning"
                className="ml-2 flex-shrink-0"
                title={t("errorLogs:errorLogs.suppressed", {
                  count: suppressed,
                })}
              >
                ×{suppressed + 1}
              </Badge>
            )}
          </div>
        </td>
        <td className="text-truncate">
          {clientId ? (
            <Button
              variant="link"
              size="sm"
              className="p-0 text-truncate mw-100"
              title={client?.title ?? clientId}
              onClick={() => onClientClick(clientId)}
            >
              {client?.title ?? clientId}
            </Button>
          ) : (
            "—"
          )}
        </td>
        <td className="text-nowrap">
          <div className="d-flex">
            <Link to={`errorLogs/${level}/${id}`}>
              <Button size="sm" variant="primary">
                {t("common:view")}
              </Button>
            </Link>
          </div>
        </td>
      </tr>
    </>
  );
};

const ErrorsLog = React.memo(() => {
  const location = useLocation();
  const history = useHistory();

  const { t } = useTranslation(["errorLogs", "common"]);

  const query = useMemo(
    () =>
      qs.parse(location.search, { parseNumbers: true }) as {
        page?: number;
        search?: string;
        startDate?: Date;
        endDate?: Date;
        logErrorType?: TLogErrorType;
        source?: ErrorLogSource;
        clientId?: string;
        requestId?: string;
      },
    [location.search],
  );
  const [search, setSearch] = useState(query.search);

  const [source, setSource] = useState<ErrorLogSource | undefined>(
    query.source,
  );

  // Set by clicking a client in the list or "all rows of this request" in a
  // log, and cleared from the badge above the table.
  const { clientId, requestId } = query;

  const [startDate, setStartDate] = useState(
    query.startDate
      ? new Date(dayjs(query.startDate).local().format())
      : new Date(dayjs().subtract(3, "day").local().format()),
  );
  const [endDate, setEndDate] = useState(
    query.endDate
      ? new Date(dayjs(query.endDate).local().format())
      : new Date(),
  );

  const [dateError, setDateError] = useState(false);

  const [logErrorType, setLogErrorType] = useState<TLogErrorType>(
    query.logErrorType || "error",
  );

  const page = useMemo(() => Math.max(query.page || 0, 1), [query.page]);

  const variables = useMemo(() => {
    const nextVariables = {
      type: logErrorType,
      pagination: {
        limit,
        skip: (page - 1) * limit,
      },
      ...(startDate || endDate || search || source || clientId || requestId
        ? {
            filter: {
              ...((startDate || endDate) && {
                createdAt: {
                  ...(startDate && {
                    GTE: dayjs(startDate).hour(0).minute(0).second(0).format(),
                  }),
                  ...(endDate && {
                    LTE: dayjs(endDate).hour(23).minute(59).second(59).format(),
                  }),
                },
              }),
              search: search?.toString(),
              ...(source && { source }),
              ...(clientId && { clientId: String(clientId) }),
              ...(requestId && { requestId: String(requestId) }),
            },
          }
        : {}),
    };

    return nextVariables;
  }, [
    page,
    startDate,
    endDate,
    search,
    logErrorType,
    source,
    clientId,
    requestId,
  ]);

  const { data } = useQuery<{
    errorLogs?: Array<IErrorLog>;
    errorLogsCount: number;
  }>(QUERY_ERROR_LOGS, {
    fetchPolicy: "network-only",
    variables,
  });

  const logs = data?.errorLogs ?? [];
  const errorsCount = data?.errorLogsCount ?? 0;

  const logErrorTypeOptions = LogErrorTypes.map((type) => ({
    value: type,
    label: type,
  }));

  const { data: clientsData } = useQuery<{ clients: IClient[] }>(
    QUERY_CLIENTS_FOR_SIDEBAR_FOR_ADMIN,
    { fetchPolicy: "cache-first" },
  );

  const clientOptions = useMemo(
    () =>
      (clientsData?.clients ?? [])
        .map((client) => ({ value: client.id, label: client.title }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [clientsData],
  );

  const sourceOptions = ErrorLogSources.map((value) => ({
    value,
    label: t(`errorLogs:errorLogs.source.${value}`),
  }));

  const pushFilters = useCallback(
    (next: { clientId?: string; requestId?: string }) => {
      history.push({
        search: qs.stringify({
          page: 1,
          ...(search && { search }),
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
          ...(logErrorType && { logErrorType }),
          ...(source && { source }),
          ...(next.clientId && { clientId: next.clientId }),
          ...(next.requestId && { requestId: next.requestId }),
        }),
      });
    },
    [history, search, startDate, endDate, logErrorType, source],
  );

  const renderLog = useCallback(
    (item: IErrorLog) => (
      <TableRow
        key={item.id}
        item={item}
        onClientClick={(nextClientId) =>
          pushFilters({ clientId: nextClientId, requestId })
        }
      />
    ),
    [pushFilters, requestId],
  );

  useDebounce(
    () => {
      if (
        search !== undefined ||
        startDate !== undefined ||
        endDate !== undefined ||
        logErrorType !== undefined
      ) {
        pushFilters({ clientId, requestId });
      }
    },
    500,
    [search, startDate, endDate, source],
  );

  useEffect(() => {
    setDateError(false);
    if (dayjs(startDate).isAfter(dayjs(endDate))) {
      setDateError(true);
    }
  }, [startDate, endDate]);

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item active" aria-current="page">
            {t("errorLogs:errorLogs.nav.errorLogs")}
          </li>
        </ol>
      </nav>
      <div className="d-flex flex-wrap mb-4" style={{ rowGap: "0.5rem" }}>
        <div className="d-flex mr-4">
          <input
            className="form-control mr-5"
            placeholder={t("common:search")}
            value={search}
            onChange={({ target: { value } }) => {
              setSearch(value);
            }}
          />
          <div className="mx-2 d-flex align-items-center">
            {t("logs:logs.date.from")}
          </div>
          <DatePicker
            isClearable
            selected={startDate}
            onChange={(date: Date) => {
              setStartDate(date);
              if (date.toDateString() === new Date().toDateString()) {
                setEndDate(date);
              }
            }}
            placeholderText="Start Date"
            dateFormat="dd/MM/yyyy"
            className="form-control"
            todayButton="Today"
          />
        </div>
        <div className="d-flex">
          <div className="mr-2 d-flex align-items-center">
            {t("logs:logs.date.to")}
          </div>
          <DatePicker
            isClearable
            selected={endDate}
            onChange={(date: Date) => {
              setEndDate(date);
            }}
            filterDate={(date) => {
              if (!!startDate) {
                return dayjs(date) >= dayjs(startDate);
              }
              return true;
            }}
            placeholderText="End Date"
            dateFormat="dd/MM/yyyy"
            className={clsx("form-control", {
              "is-invalid": dateError,
            })}
          />
        </div>
        <div className="d-flex ml-5">
          <label htmlFor="roles" className="mb-0 mr-2 align-self-center">
            {t("logs:logs.type")}
          </label>
          <div style={{ width: "13rem" }}>
            <Select
              isClearable
              options={logErrorTypeOptions}
              value={logErrorTypeOptions.find(
                (option) => option.value === logErrorType,
              )}
              onChange={(value) => {
                if (value) {
                  setLogErrorType(value.value);
                }
              }}
              styles={reactSelectCustomStyles(false)}
            />
          </div>
        </div>
        <div className="d-flex ml-4">
          <label htmlFor="source" className="mb-0 mr-2 align-self-center">
            {t("errorLogs:errorLogs.source.label")}
          </label>
          <div style={{ width: "11rem" }}>
            <Select
              inputId="source"
              isClearable
              options={sourceOptions}
              value={
                sourceOptions.find((option) => option.value === source) ?? null
              }
              onChange={(value) => setSource(value?.value)}
              styles={reactSelectCustomStyles(false)}
            />
          </div>
        </div>
        <div className="d-flex ml-4">
          <label htmlFor="client" className="mb-0 mr-2 align-self-center">
            {t("errorLogs:errorLogs.th.client")}
          </label>
          <div style={{ width: "13rem" }}>
            <Select
              inputId="client"
              isClearable
              options={clientOptions}
              value={
                clientOptions.find(
                  (option) => option.value === String(clientId),
                ) ?? null
              }
              onChange={(value) =>
                pushFilters({ clientId: value?.value, requestId })
              }
              styles={reactSelectCustomStyles(false)}
            />
          </div>
        </div>
      </div>
      {!!requestId && (
        <div className="mb-3">
          <Badge variant="info" className="p-2">
            {t("errorLogs:errorLogs.filter.request", { requestId })}
            <Button
              variant="link"
              size="sm"
              className="p-0 ml-2 text-white"
              onClick={() => pushFilters({ clientId })}
            >
              ×
            </Button>
          </Badge>
        </div>
      )}
      <Table striped bordered hover responsive={false} className="table-fixed">
        <thead>
          <tr>
            <th scope="col" style={{ width: "11rem" }}>
              {t("errorLogs:errorLogs.th.created")}
            </th>
            <th scope="col" style={{ width: "28%" }}>
              {t("errorLogs:errorLogs.th.operation")}
            </th>
            <th scope="col">{t("errorLogs:errorLogs.th.errorMessage")}</th>
            <th scope="col" style={{ width: "12rem" }}>
              {t("errorLogs:errorLogs.th.client")}
            </th>
            <th scope="col" style={{ width: "7rem" }}>
              {t("errorLogs:errorLogs.th.actions")}
            </th>
          </tr>
        </thead>
        <tbody>{logs.map(renderLog)}</tbody>
      </Table>
      <Pagination documentsCount={errorsCount} limit={limit} />
    </div>
  );
});

const ErrorsRoute = React.memo(() => {
  const { path } = useRouteMatch();

  return (
    <Switch>
      <Route exact path={path}>
        <ErrorsLog />
      </Route>
      <Route path={`${path}/:type/:id`}>
        <ErrorLog />
      </Route>
    </Switch>
  );
});

export default ErrorsRoute;
